/**
 * Centralized variable state management.
 *
 * This is a facade that composes focused components:
 * - VariableStore: Pure state storage for variables
 * - OverrideManager: Block and script-level variable overrides
 * - MetadataTracker: Code block and script metadata
 *
 * Provides a unified API for backward compatibility.
 */

import { VariableStore, type VariablesResult } from './VariableStore.js';
import { OverrideManager } from './OverrideManager.js';
import { MetadataTracker, type CodeBlockMeta, type ScriptMeta } from './MetadataTracker.js';

// Re-export types for convenience
export type { CodeBlockMeta, ScriptMeta, VariablesResult };

export class VariableSystem {
  private store: VariableStore;
  private overrides: OverrideManager;
  private metadata: MetadataTracker;

  constructor() {
    this.store = new VariableStore();
    this.overrides = new OverrideManager(this.store);
    this.metadata = new MetadataTracker();
  }

  // ============ Variable State (delegated to VariableStore) ============

  /** Static variables from frontmatter (no @ prefix) */
  get staticVariables(): Record<string, string> {
    return this.store.staticVariables;
  }

  /** Dynamic variables from frontmatter (@ prefixed) */
  get dynamicVariables(): Record<string, string> {
    return this.store.dynamicVariables;
  }

  /** Merged variables with user edits applied */
  get userVariables(): Record<string, string> {
    return this.store.userVariables;
  }

  /**
   * Set variables from parsed frontmatter.
   */
  setVariables(staticVars: Record<string, string>, dynamicVars: Record<string, string>): void {
    this.store.setVariables(staticVars, dynamicVars);
  }

  /**
   * Update a user variable value (from the global variables form).
   */
  setUserVariable(varName: string, value: string): void {
    this.store.setUserVariable(varName, value);
  }

  /**
   * Restore variable state including the user's edited values (e.g. when
   * switching back to a cached tab). `setVariables` alone resets user values
   * to the frontmatter defaults, which would make scripts (rendered via
   * `applyScriptVariables`) silently revert to defaults while the variables
   * form still displays the user's override.
   */
  restoreVariables(
    staticVars: Record<string, string>,
    dynamicVars: Record<string, string>,
    userVars: Record<string, string>
  ): void {
    this.store.setVariables(staticVars, dynamicVars);
    for (const [name, value] of Object.entries(userVars)) {
      this.store.setUserVariable(name, value);
    }
  }

  /**
   * Extract variables from frontmatter content.
   */
  extractVariablesFromContent(content: string): VariablesResult {
    return this.store.extractVariablesFromContent(content);
  }

  // ============ Block Overrides (delegated to OverrideManager) ============

  /**
   * Get the effective value for a variable, considering block overrides.
   */
  getValue(varName: string, blockIndex: number | null = null): string | undefined {
    return this.overrides.getBlockValue(varName, blockIndex);
  }

  /**
   * Set a per-block variable override.
   */
  setBlockOverride(blockIndex: number, varName: string, value: string): void {
    this.overrides.setBlockOverride(blockIndex, varName, value);
  }

  /**
   * Check if a block has any local overrides.
   */
  hasBlockOverrides(blockIndex: number): boolean {
    return this.overrides.hasBlockOverrides(blockIndex);
  }

  /**
   * Get all overrides for a block.
   */
  getBlockOverrides(blockIndex: number): Record<string, string> {
    return this.overrides.getBlockOverrides(blockIndex);
  }

  /**
   * Clear all overrides for a specific block.
   */
  clearBlockOverrides(blockIndex: number): void {
    this.overrides.clearBlockOverrides(blockIndex);
  }

  /**
   * Clear all block overrides (e.g., when loading a new file).
   */
  clearAllBlockOverrides(): void {
    this.overrides.clearAllBlockOverrides();
  }

  /**
   * Apply variable substitution to a template string with block-level overrides.
   */
  applyVariables(template: string, blockIndex: number | null = null): string {
    return this.overrides.applyBlockVariables(template, blockIndex);
  }

  // ============ Script Overrides (delegated to OverrideManager) ============

  /**
   * Set a per-script variable override.
   */
  setScriptOverride(scriptName: string, varName: string, value: string): void {
    this.overrides.setScriptOverride(scriptName, varName, value);
  }

  /**
   * Check if a script has any local overrides.
   */
  hasScriptOverrides(scriptName: string): boolean {
    return this.overrides.hasScriptOverrides(scriptName);
  }

  /**
   * Get all overrides for a script.
   */
  getScriptOverrides(scriptName: string): Record<string, string> {
    return this.overrides.getScriptOverrides(scriptName);
  }

  /**
   * Clear all overrides for a specific script.
   */
  clearScriptOverrides(scriptName: string): void {
    this.overrides.clearScriptOverrides(scriptName);
  }

  /**
   * Clear all script overrides (e.g., when loading a new file).
   */
  clearAllScriptOverrides(): void {
    this.overrides.clearAllScriptOverrides();
  }

  /**
   * Get the effective value for a variable in a script context.
   */
  getScriptValue(varName: string, scriptName: string | null = null): string | undefined {
    return this.overrides.getScriptValue(varName, scriptName);
  }

  /**
   * Apply variable substitution to a script template string with script-level overrides.
   */
  applyScriptVariables(template: string, scriptName: string | null = null): string {
    return this.overrides.applyScriptVariables(template, scriptName);
  }

  // ============ Metadata (delegated to MetadataTracker) ============

  /** Code block metadata */
  get codeBlockMeta(): CodeBlockMeta[] {
    return this.metadata.codeBlockMeta;
  }

  /** Script metadata */
  get scriptMeta(): ScriptMeta[] {
    return this.metadata.scriptMeta;
  }

  /**
   * Set code block metadata (parsed from source before weaving).
   */
  setCodeBlockMeta(meta: CodeBlockMeta[]): void {
    this.metadata.setCodeBlockMeta(meta);
  }

  /**
   * Get metadata for a specific code block.
   */
  getCodeBlockMeta(blockIndex: number): CodeBlockMeta | undefined {
    return this.metadata.getCodeBlockMeta(blockIndex);
  }

  /**
   * Parse source content to extract code block variable metadata.
   */
  parseCodeBlockVariables(sourceContent: string): CodeBlockMeta[] {
    return this.metadata.parseCodeBlockVariables(sourceContent);
  }

  /**
   * Set script metadata.
   */
  setScriptMeta(meta: ScriptMeta[]): void {
    this.metadata.setScriptMeta(meta);
  }

  /**
   * Get metadata for a specific script.
   */
  getScriptMeta(scriptName: string): ScriptMeta | undefined {
    return this.metadata.getScriptMeta(scriptName);
  }

  // ============ Reset ============

  /**
   * Reset all state (for loading a new file).
   */
  reset(): void {
    this.store.reset();
    this.overrides.reset();
    this.metadata.reset();
  }
}
